import React, { useState, useEffect, useContext } from "react";
import { Container, Nav, Navbar, Button, Offcanvas } from "react-bootstrap";
import { NavLink, useNavigate } from "react-router-dom";
import "../../../assets/css/gcstudent/Navbar/GcNavbar.css";
import logo from "../../../assets/images/logo.jpg.jpeg";
import AppContext from "../../../context/AppContext"; 

const navLinks = [
  { label: "Home", path: "/" },
  { label: "Library", path: "/library" },
  { label: "Digital Library", path: "/digital-library" },
  { label: "Notes", path: "/notes" },
  { label: "Blogs", path: "/blogs" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
];

const GcNavbar = () => {
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const handleClose = () => setShowOffcanvas(false);
  const handleShow = () => setShowOffcanvas(true);
  const navigate = useNavigate();

  const { loggedInUser, userType } = useContext(AppContext);

  // Only student/teacher accounts show a profile icon on this public navbar —
  // admin accounts are managed through their own separate panel/topbar.
  const isLoggedIn = Boolean(loggedInUser && (userType === "student" || userType === "teacher"));

  // Navigate then close the offcanvas (mobile menu)
  const handleMobileNavigate = (path) => {
    navigate(path);
    handleClose();
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Dashboard route based on userType — adjust paths if yours are different
  const dashboardRouteByType = {
    student: "/student/dashboard",
    teacher: "/teacher/dashboard",
  };

  const goToDashboard = () => {
    const route = dashboardRouteByType[userType];
    if (route) navigate(route);
  };

  const goToDashboardMobile = () => {
    const route = dashboardRouteByType[userType];
    if (route) {
      navigate(route);
      handleClose();
    }
  };

  // Initials avatar fallback (e.g. "Ali Khan" -> "AK")
  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    return parts.length > 1
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  };

  const userName = loggedInUser?.fullName || loggedInUser?.name || "";

  return (
    <>
      <Navbar
        expand="lg"
        className={`gcStudent-navbar ${scrolled ? "gcStudent-navbar--scrolled" : ""}`}
        fixed="top"
      >
        <Container fluid="xl">
          {/* Brand */}
          <Navbar.Brand as={NavLink} to="/" className="gcStudent-brand">
            <div className="gcStudent-brand-icon">
              <img src={logo} alt="GGCW Logo" className="gcStudent-logo" />
            </div>
            <span className="gcStudent-brand-name">GGCW Library</span>
          </Navbar.Brand>

          {/* Desktop Nav Links */}
          <Nav className="gcStudent-nav-links mx-auto d-none d-lg-flex">
            {navLinks.map((link) => (
              <NavLink
                key={link.label}
                to={link.path}
                className={({ isActive }) =>
                  `gcStudent-nav-link ${isActive ? "gcStudent-nav-link--active" : ""}`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </Nav>

          {/* Desktop Auth Buttons / Profile Icon */}
          <div className="gcStudent-auth-buttons d-none d-lg-flex">
            {isLoggedIn ? (
              <button
                className="gcStudent-profile-btn"
                onClick={goToDashboard}
                title={userName || "My Dashboard"}
                aria-label="Go to dashboard"
              >
                {getInitials(userName)}
              </button>
            ) : (
              <>
                <Button
                  variant="outline"
                  className="gcStudent-btn-student"
                  onClick={() => navigate("/student/login")}
                >
                  Student
                </Button>
                <Button
                  className="gcStudent-btn-staff"
                  onClick={() => navigate("/teacher/login")}
                >
                  Teacher
                </Button>
              </>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`gcStudent-hamburger d-lg-none ${showOffcanvas ? "gcStudent-hamburger--open" : ""}`}
            onClick={handleShow}
            aria-label="Toggle navigation"
          >
            <span className="gcStudent-hamburger-line"></span>
            <span className="gcStudent-hamburger-line"></span>
            <span className="gcStudent-hamburger-line"></span>
          </button>
        </Container>
      </Navbar>

      {/* Mobile Offcanvas */}
      <Offcanvas
        show={showOffcanvas}
        onHide={handleClose}
        placement="start"
        className="gcStudent-offcanvas"
      >
        <Offcanvas.Header className="gcStudent-offcanvas-header">
          <div className="gcStudent-brand">
            <div className="gcStudent-brand-icon">
              <img src={logo} alt="GGCW Logo" className="gcStudent-logo" />
            </div>
            <span className="gcStudent-brand-name">GGCW Library</span>
          </div>
          <button
            className="gcStudent-offcanvas-close"
            onClick={handleClose}
            aria-label="Close menu"
          >
            ✕
          </button>
        </Offcanvas.Header>

        <Offcanvas.Body className="gcStudent-offcanvas-body">
          <Nav className="gcStudent-mobile-nav flex-column">
            {navLinks.map((link, index) => (
              <NavLink
                key={link.label}
                to={link.path}
                className={({ isActive }) =>
                  `gcStudent-mobile-nav-link ${isActive ? "gcStudent-mobile-nav-link--active" : ""}`
                }
                onClick={handleClose}
                style={{ animationDelay: `${index * 0.06}s` }}
              >
                {link.label}
              </NavLink>
            ))}
          </Nav>

          {/* Mobile Auth Buttons / Profile Icon */}
          <div className="gcStudent-mobile-auth">
            {isLoggedIn ? (
              <button
                className="gcStudent-profile-btn gcStudent-profile-btn--mobile w-100"
                onClick={goToDashboardMobile}
              >
                <span className="gcStudent-profile-avatar">
                  {getInitials(userName)}
                </span>
                <span>{userName || "My Dashboard"}</span>
              </button>
            ) : (
              <>
                <Button
                  variant="outline"
                  className="gcStudent-btn-student w-100"
                  onClick={() => handleMobileNavigate("/student/login")}
                >
                  Student
                </Button>
                <Button
                  className="gcStudent-btn-staff w-100"
                  onClick={() => handleMobileNavigate("/teacher/login")}
                >
                  Teacher
                </Button>
              </>
            )}
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default GcNavbar;